import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Loader2, X } from "lucide-react";
import {
  getCreatorBudget,
  getOfferedOdds,
  getTotalCounterStake,
  getUsedSlots,
} from "../../utils/p2pHelpers";

const MIN_OFFERED_ODDS = 1.01;
const MAX_OFFERED_ODDS = 50;
const MIN_CREATOR_BUDGET = 500;

const validate = (budgetRaw, oddsRaw, bet) => {
  if (!budgetRaw || !oddsRaw) return "Both fields are required.";
  const budget = parseFloat(budgetRaw);
  const odds = parseFloat(oddsRaw);
  const currentBudget = getCreatorBudget(bet);
  const minBudget = Math.max(currentBudget, MIN_CREATOR_BUDGET);

  if (Number.isNaN(budget) || budget < minBudget) {
    return `Budget cannot be below your current allocation (Cash ${minBudget}).`;
  }
  if (Number.isNaN(odds) || odds < MIN_OFFERED_ODDS || odds > MAX_OFFERED_ODDS) {
    return `Odds must be between ${MIN_OFFERED_ODDS} and ${MAX_OFFERED_ODDS}.`;
  }
  return "";
};

const EditP2PModal = ({ open, onClose, onSubmit, bet, loading = false }) => {
  const [creatorBudget, setCreatorBudget] = useState("");
  const [offeredOdds, setOfferedOdds] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (open && bet) {
      setCreatorBudget(String(getCreatorBudget(bet) || ""));
      setOfferedOdds(String(getOfferedOdds(bet) || ""));
      setError("");
    }
  }, [open, bet]);

  const validationMsg = useMemo(() => {
    if (!bet) return "Offer not found.";
    return validate(creatorBudget, offeredOdds, bet);
  }, [creatorBudget, offeredOdds, bet]);

  if (!open || !bet) return null;

  const disabled = !!validationMsg || loading;
  const hasCounters = getTotalCounterStake(bet) > 0;
  const budgetDelta = parseFloat(creatorBudget) - getCreatorBudget(bet);

  const handleSubmit = async () => {
    const msg = validate(creatorBudget, offeredOdds, bet);
    if (msg) {
      setError(msg);
      return;
    }
    try {
      await onSubmit({
        creatorBudget: parseFloat(creatorBudget),
        offeredOdds: parseFloat(offeredOdds),
      });
      onClose();
    } catch (err) {
      setError(err?.message || "Could not update this wager.");
    }
  };

  return createPortal(
    <div className="p2p-modal-root" role="dialog" aria-modal="true">
      <button type="button" className="p2p-modal-backdrop" aria-label="Close" onClick={onClose} />
      <div className="p2p-modal-sheet">
        <header className="p2p-modal-head">
          <div>
            <p className="p2p-modal-eyebrow">Edit P2P</p>
            <h2 className="p2p-modal-title">Budget & odds</h2>
          </div>
          <button type="button" className="p2p-modal-close" onClick={onClose} aria-label="Close">
            <X size={16} />
          </button>
        </header>

        <p className="p2p-modal-help">
          Increase your allocated budget and adjust odds for new counters.
          Existing counter bets keep their original stake and payout.
        </p>

        {hasCounters ? (
          <p className="p2p-modal-note">
            {getUsedSlots(bet)} counter{getUsedSlots(bet) === 1 ? "" : "s"} matched — their bets will not change.
          </p>
        ) : null}

        <div className="p2p-modal-fields">
          <label className="p2p-modal-label">Creator budget (Cash)</label>
          <div className="p2p-modal-input-wrap">
            <span className="p2p-modal-input-badge">Cash</span>
            <input
              type="text"
              inputMode="decimal"
              value={creatorBudget}
              onChange={(e) => {
                setCreatorBudget(e.target.value.replace(/[^0-9.]/g, ""));
                setError("");
              }}
              placeholder={String(getCreatorBudget(bet) || MIN_CREATOR_BUDGET)}
            />
          </div>

          <label className="p2p-modal-label">Offered odds</label>
          <div className="p2p-modal-input-wrap">
            <input
              type="text"
              inputMode="decimal"
              value={offeredOdds}
              onChange={(e) => {
                setOfferedOdds(e.target.value.replace(/[^0-9.]/g, ""));
                setError("");
              }}
              placeholder={String(getOfferedOdds(bet) || "2")}
            />
          </div>

          {budgetDelta > 0 ? (
            <p className="p2p-modal-hint">+{budgetDelta} Cash will be locked from your balance.</p>
          ) : null}

          {error && <p className="p2p-modal-error">{error}</p>}
        </div>

        <footer className="p2p-modal-foot">
          <button type="button" className="p2p-modal-btn p2p-modal-btn-ghost" onClick={onClose}>
            Back
          </button>
          <button
            type="button"
            className="p2p-modal-btn p2p-modal-btn-primary"
            disabled={disabled}
            onClick={handleSubmit}
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : "Save changes"}
          </button>
        </footer>
      </div>
    </div>,
    document.body
  );
};

export default EditP2PModal;
